import React, { useState, useEffect } from 'react';
import {
  StickyNote,
  Plus,
  Check,
  Trash2,
  Pin,
  PinOff,
  MessageSquare,
  ListTodo,
  Loader2,
  X
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { apiRequest } from '../lib/utils';
import { toast } from 'sonner';

const CATEGORIES = [
  { id: 'todo', label: 'To Do', icon: ListTodo, color: 'bg-blue-100 text-blue-700' },
  { id: 'discuss', label: 'To Discuss', icon: MessageSquare, color: 'bg-purple-100 text-purple-700' },
  { id: 'general', label: 'General', icon: StickyNote, color: 'bg-slate-100 text-slate-700' }
];

export default function NotesWidget() {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAdding, setIsAdding] = useState(false);
  const [newNote, setNewNote] = useState('');
  const [newCategory, setNewCategory] = useState('todo');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchNotes();
  }, []);

  const fetchNotes = async () => {
    try {
      const data = await apiRequest('/notes');
      setNotes(data);
    } catch (error) {
      console.error('Failed to fetch notes:', error);
    } finally {
      setLoading(false);
    }
  };

  const addNote = async () => {
    if (!newNote.trim()) return;
    
    setSaving(true);
    try {
      const note = await apiRequest('/notes', {
        method: 'POST',
        body: JSON.stringify({
          content: newNote.trim(),
          category: newCategory
        })
      });
      setNotes([note, ...notes]);
      setNewNote('');
      setIsAdding(false);
      toast.success('Note added');
    } catch (error) {
      toast.error('Failed to add note');
    } finally {
      setSaving(false);
    }
  };

  const toggleComplete = async (noteId) => {
    try {
      const result = await apiRequest(`/notes/${noteId}/toggle-complete`, {
        method: 'POST'
      });
      setNotes(notes.map(n => 
        n.id === noteId ? { ...n, is_completed: result.is_completed } : n
      ));
    } catch (error) {
      toast.error('Failed to update note');
    }
  };

  const togglePin = async (noteId) => {
    try {
      const result = await apiRequest(`/notes/${noteId}/toggle-pin`, {
        method: 'POST'
      });
      setNotes(notes.map(n => 
        n.id === noteId ? { ...n, is_pinned: result.is_pinned } : n
      ).sort((a, b) => {
        if (a.is_pinned && !b.is_pinned) return -1;
        if (!a.is_pinned && b.is_pinned) return 1;
        return 0;
      }));
    } catch (error) {
      toast.error('Failed to pin note');
    }
  };

  const deleteNote = async (noteId) => {
    try {
      await apiRequest(`/notes/${noteId}`, { method: 'DELETE' });
      setNotes(notes.filter(n => n.id !== noteId));
      toast.success('Note deleted');
    } catch (error) {
      toast.error('Failed to delete note');
    }
  };

  const getCategoryInfo = (categoryId) => {
    return CATEGORIES.find(c => c.id === categoryId) || CATEGORIES[2];
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="py-8 flex items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="notes-widget">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <StickyNote className="h-5 w-5 text-amber-500" />
            <CardTitle className="text-lg">My Notes</CardTitle>
            <Badge variant="secondary" className="ml-1">{notes.length}</Badge>
          </div>
          {!isAdding && (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setIsAdding(true)}
              data-testid="add-note-btn"
            >
              <Plus className="h-4 w-4 mr-1" />
              Add
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Add Note Form */}
        {isAdding && (
          <div className="p-3 bg-amber-50 rounded-lg border border-amber-200 space-y-3">
            <Textarea
              value={newNote}
              onChange={(e) => setNewNote(e.target.value)}
              placeholder="What's on your mind?"
              rows={2}
              className="bg-white"
              autoFocus
              data-testid="new-note-input"
            />
            <div className="flex items-center justify-between">
              <div className="flex gap-1">
                {CATEGORIES.map(cat => (
                  <button
                    key={cat.id}
                    onClick={() => setNewCategory(cat.id)}
                    className={`px-2 py-1 rounded text-xs font-medium transition-all ${
                      newCategory === cat.id 
                        ? cat.color + ' ring-2 ring-offset-1 ring-amber-400' 
                        : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                    }`}
                  >
                    {cat.label}
                  </button>
                ))}
              </div>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => { setIsAdding(false); setNewNote(''); }}
                >
                  <X className="h-4 w-4" />
                </Button>
                <Button
                  size="sm"
                  onClick={addNote}
                  disabled={!newNote.trim() || saving}
                  data-testid="save-note-btn"
                >
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save'}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Notes List */}
        {notes.length === 0 && !isAdding ? (
          <div className="text-center py-6 text-slate-400">
            <StickyNote className="h-10 w-10 mx-auto mb-2 opacity-50" />
            <p className="text-sm">No notes yet</p>
            <p className="text-xs">Click Add to create your first note</p>
          </div>
        ) : (
          <div className="space-y-2 max-h-[300px] overflow-y-auto pr-1">
            {notes.map(note => {
              const category = getCategoryInfo(note.category);
              const CategoryIcon = category.icon;
              
              return (
                <div
                  key={note.id}
                  className={`group p-3 rounded-lg border transition-all ${
                    note.is_completed 
                      ? 'bg-slate-50 border-slate-200' 
                      : note.is_pinned 
                        ? 'bg-amber-50 border-amber-200' 
                        : 'bg-white border-slate-200 hover:border-slate-300'
                  }`}
                  data-testid={`note-item-${note.id}`}
                >
                  <div className="flex items-start gap-2">
                    {/* Checkbox */}
                    <button
                      onClick={() => toggleComplete(note.id)}
                      className={`mt-0.5 w-5 h-5 rounded border flex-shrink-0 flex items-center justify-center transition-all ${
                        note.is_completed
                          ? 'bg-green-500 border-green-500 text-white'
                          : 'border-slate-300 hover:border-green-400'
                      }`}
                      data-testid={`toggle-complete-${note.id}`}
                    >
                      {note.is_completed && <Check className="h-3 w-3" />}
                    </button>
                    
                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm whitespace-pre-wrap break-words ${
                        note.is_completed ? 'text-slate-400 line-through' : 'text-slate-700'
                      }`}>
                        {note.content}
                      </p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs ${category.color}`}>
                          <CategoryIcon className="h-3 w-3" />
                          {category.label}
                        </span>
                        {note.is_pinned && (
                          <Pin className="h-3 w-3 text-amber-500" />
                        )}
                      </div>
                    </div>
                    
                    {/* Actions */}
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        onClick={() => togglePin(note.id)}
                        className="p-1 rounded hover:bg-slate-100"
                        title={note.is_pinned ? 'Unpin' : 'Pin'}
                        data-testid={`toggle-pin-${note.id}`}
                      >
                        {note.is_pinned ? (
                          <PinOff className="h-4 w-4 text-amber-500" />
                        ) : (
                          <Pin className="h-4 w-4 text-slate-400" />
                        )}
                      </button>
                      <button
                        onClick={() => deleteNote(note.id)}
                        className="p-1 rounded hover:bg-red-50"
                        title="Delete"
                        data-testid={`delete-note-${note.id}`}
                      >
                        <Trash2 className="h-4 w-4 text-slate-400 hover:text-red-500" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
